// LegalPage
// =========
//
// Wave 14ca-fix27: Impressum + Datenschutzerklärung. Eine einzige Page
// die zwei Sektionen rendert (Imprint + Privacy), erreichbar:
//   - aus ProfilePage (immer zugänglich)
//   - aus OnboardingPermissions (Erst-Aufruf, vor "Continue")
//
// WICHTIG: die Texte unten sind Vorlagen / Platzhalter. Bitte mit echten
// Daten füllen vor Production-Launch — sonst Risiko bei Beschwerde /
// Audit. Speziell zu prüfen:
//   - Verantwortlicher (Name + Anschrift)
//   - Kontakt (E-Mail / Telefon)
//   - Rechtsform (privat? GmbH? UG?)
//   - USt-IdNr. (falls gewerblich)
//   - DSB (Datenschutzbeauftragter, falls erforderlich)
//   - tatsächlich verarbeitete Datenkategorien (siehe TODOs unten)

import { useSearchParams, Link } from 'react-router-dom';

export function LegalPage() {
  const [params] = useSearchParams();
  const tab = params.get('tab') === 'privacy' ? 'privacy' : 'imprint';
  return (
    <div className="absolute inset-0 overflow-y-auto bg-turbo-900 safe-top">
      <div className="px-4 pb-10 pt-4 max-w-2xl mx-auto">
        <Link
          to="/profile"
          className="inline-block text-sky-400 text-sm no-underline mb-3"
        >
          ← Profile
        </Link>

        <div className="flex gap-2 mb-5 border-b border-turbo-700/40">
          <TabLink to="/legal?tab=imprint" active={tab === 'imprint'}>
            Imprint
          </TabLink>
          <TabLink to="/legal?tab=privacy" active={tab === 'privacy'}>
            Privacy
          </TabLink>
        </div>

        {tab === 'imprint' ? <ImprintSection /> : <PrivacySection />}
      </div>
    </div>
  );
}

function TabLink({
  to,
  active,
  children,
}: {
  to: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className={`px-3 py-2 text-sm no-underline border-b-2 -mb-[1px] ${
        active
          ? 'text-white border-sky-500 font-semibold'
          : 'text-white/55 border-transparent'
      }`}
    >
      {children}
    </Link>
  );
}

/* ---- IMPRESSUM ---- */
// TODO(legal): mit echten Daten ersetzen. Pflichtangaben nach §5 TMG +
// §55 RStV (Deutschland). Wenn Anbieter ausserhalb DE → andere Pflichten,
// vor Launch mit Anwalt klären.
function ImprintSection() {
  return (
    <div className="text-white/85 text-sm leading-relaxed space-y-4">
      <h1 className="text-2xl font-bold text-white">Imprint</h1>

      <section>
        <h2 className="text-white font-semibold mb-1">Provider</h2>
        <p>
          Tom Janas
          <br />
          {/* TODO: street + house number */}
          [Street, house number]
          <br />
          {/* TODO: zip + city */}
          [ZIP, City]
          <br />
          Germany
        </p>
      </section>

      <section>
        <h2 className="text-white font-semibold mb-1">Contact</h2>
        <p>
          Email:{' '}
          <a
            href="mailto:admin@tomjanas.com"
            className="text-orange-400 no-underline"
          >
            admin@tomjanas.com
          </a>
        </p>
        {/* TODO: optional phone */}
      </section>

      <section>
        <h2 className="text-white font-semibold mb-1">
          Responsible for content under §55 (2) RStV
        </h2>
        <p>Tom Janas (address as above)</p>
      </section>

      <section>
        <h2 className="text-white font-semibold mb-1">Liability notice</h2>
        <p>
          Despite careful content review, we assume no liability for the
          content of external links. The operators of the linked pages are
          solely responsible for their content. Routes and POIs are
          rendered based on OpenStreetMap and SRTM data; accuracy cannot
          be guaranteed. MTB trails are physically demanding — ride at
          your own risk.
        </p>
      </section>

      <section>
        <h2 className="text-white font-semibold mb-1">Data sources / credits</h2>
        <p>
          Maps © OpenStreetMap contributors, elevation data © NASA SRTM,
          hillshade rendering based on Mapbox styles.
        </p>
      </section>
    </div>
  );
}

/* ---- DATENSCHUTZ ---- */
// TODO(legal): vor Launch mit Anwalt prüfen. Insbesondere:
//   - Push-Subscriptions (endpoint URLs sind technisch personenbezogen)
//   - Akkuverbrauchs-Daten (GPS-Tracks = Bewegungsprofil → besonders sensibel)
//   - Analytics? (aktuell keine, falls eingeführt → hier ergänzen)
function PrivacySection() {
  return (
    <div className="text-white/85 text-sm leading-relaxed space-y-4">
      <h1 className="text-2xl font-bold text-white">Privacy policy</h1>

      <p className="text-white/55 italic">
        As of April 2026. We handle your data as sparingly as possible — if
        something isn't strictly needed, we don't collect it.
      </p>

      <section>
        <h2 className="text-white font-semibold mb-1">What we store</h2>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            <strong className="text-white">Account:</strong> email address +
            hash of your password (for login). Never any plaintext password.
          </li>
          <li>
            <strong className="text-white">Push notifications</strong>{' '}
            (optional): if enabled, we store your browser's anonymous
            push-endpoint URL. We get NO tracking through this — we can only
            send you notifications (e.g. when a new trail is added).
          </li>
          <li>
            <strong className="text-white">Battery-Lab</strong> (optional, PRO):
            if you upload rides to learn battery usage, we store the GPX
            file and your battery readings. GPS tracks are personal data —
            we NEVER share them with other users and use them exclusively
            for your own consumption prediction.
          </li>
          <li>
            <strong className="text-white">Custom tours</strong> (optional,
            PRO): routes you draw yourself are stored locally on your
            device. Actively "uploading" them is an explicit click —
            otherwise they stay on the phone.
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-white font-semibold mb-1">What we do NOT do</h2>
        <ul className="space-y-1 list-disc pl-5">
          <li>No third-party tracking (Google Analytics, Facebook Pixel, etc.)</li>
          <li>No ads in the app</li>
          <li>No sharing of your data with third parties</li>
          <li>
            No background location tracking — location is only requested
            when you actively use a map / route
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-white font-semibold mb-1">Your rights (GDPR)</h2>
        <p>
          You have the right at any time to access, correct, delete, and
          port your data. Just write to us at{' '}
          <a
            href="mailto:admin@tomjanas.com"
            className="text-orange-400 no-underline"
          >
            admin@tomjanas.com
          </a>
          {' '}— we'll reply within 14 days.
        </p>
      </section>

      <section>
        <h2 className="text-white font-semibold mb-1">Hosting</h2>
        <p>
          Backend hosted at Hetzner Online GmbH (Germany). Push
          notifications are sent via the push services of Apple, Google,
          and Mozilla (depending on the recipient's browser).
        </p>
      </section>
    </div>
  );
}
